// Server-side i18n: the same catalogues and formatters as the SPA on a
// private i18next instance (never the module singleton, which the client and
// its tests own), plus the locale resolution the middleware uses.
import { createInstance, type InitOptions, type i18n, type TFunction } from "i18next";
import type { Locale } from "../contract/auth";
import { NOT_AVAILABLE, registerFormatters } from "./format";
import { DEFAULT_NAMESPACE, NAMESPACES, resources } from "./resources";

export { ERROR_COUNT_PARAM, errorMessage } from "./errors";
export * from "./format";
export { parseAcceptLanguage, resolveLocale } from "./locale";
export * from "./resources";

/** The init options both sides share; the client adds its react binding and the resolved language. */
export function i18nInitOptions(locale: Locale): InitOptions {
  return {
    resources,
    lng: locale,
    fallbackLng: "de",
    supportedLngs: ["de", "en"],
    ns: [...NAMESPACES],
    defaultNS: DEFAULT_NAMESPACE,
    interpolation: { escapeValue: false },
    // The same contract as the formatters: a value the screen has not got yet
    // reads as nothing. i18next hands a null or undefined value straight past
    // our formatter, and without this it leaves the raw "{{at, dateTime}}" in
    // the sentence, because the matched variable carries the format name and
    // i18next cannot see that "at" was passed at all.
    missingInterpolationHandler: () => NOT_AVAILABLE,
    returnNull: false,
  };
}

let server: i18n | undefined;

/** The server's instance, created on first use; init is synchronous because every resource is bundled. */
export function serverI18n(): i18n {
  if (server) return server;
  const instance = createInstance();
  instance.init({ ...i18nInitOptions("de"), initAsync: false });
  registerFormatters(instance);
  server = instance;
  return instance;
}

/** A t() fixed to one Interface Language, for a request or a job. */
export function translatorFor(locale: Locale): TFunction {
  return serverI18n().getFixedT(locale);
}
